import { IntlShape } from 'react-intl';
import { ParsedUrlQuery } from 'node:querystring';
import getSharedLinks, { infoFeatures, infoLinks } from './getSharedLinks';
import { infoNavigation } from './navigation';
import messages from '../lib/messages';

export interface Item {
  name?: string;
  id: string;
  href?: string;
  router?: boolean;
  params?: ParsedUrlQuery;
  route?: string;
}
interface MenuItem {
  id: string;
  items: Item[];
  router?: boolean;
  route?: string;
  href?: string;
  params?: ParsedUrlQuery;
}

type GetDrawerProperties = (
  intl: IntlShape,
  newsboard?: string,
  jobsLanding?: boolean,
  locale?: string,
  hasNews?: boolean,
  hasJobsInLanding?: boolean
) => MenuItem[];

const getDrawer: GetDrawerProperties = (
  intl, newsboard, jobsLanding, locale, hasNews, hasJobsInLanding,
) => {
  const links = newsboard === 'FASHIONUNITED_INFO' && [
    ...infoFeatures,
    ...infoNavigation,
    ...infoLinks,
  ];
  const isUS = newsboard === 'FASHIONUNITED_COM';
  const hasSpecialPages = newsboard && [
    'FASHIONUNITED_BE',
    'FASHIONUNITED_DE',
    'FASHIONUNITED_ES',
    'FASHIONUNITED_FR',
    'FASHIONUNITED_IT',
    'FASHIONUNITED_NL',
    'FASHIONUNITED_UK',
  ].includes(newsboard);

  return links || [
    {
      id: 'news',
      items: [
        {
          id: 'news-feed',
          router: hasNews,
          route: `news-feed.${locale}`,
          href: 'https://fashionunited.com/just-in',
        },
        ...getSharedLinks(intl, hasNews),
        {
          id: 'category.news.background',
          href: intl.formatMessage(
            messages['category.news.background.href'],
          ),
          router: hasNews,
          route: 'category',
          params: {
            categoryPath: intl.formatMessage(
              messages['category.news.background.param'],
            ),
          },
        },
        {
          id: 'category.news.video',
          href: intl.formatMessage(
            messages['category.news.video.href'],
          ),
          router: !hasNews,
          route: 'category',
          params: {
            categoryPath: intl.formatMessage(
              messages['category.news.video.param'],
            ),
          },
        },
        {
          id: 'category.news.podcast',
          href: intl.formatMessage(
            messages['category.news.podcast.href'],
          ),
          router: !hasNews,
          route: 'category',
          params: {
            categoryPath: intl.formatMessage(
              messages['category.news.podcast.param'],
            ),
          },
        },
        {
          id: 'category.press',
          href: intl.formatMessage(
            messages['category.press.href'],
          ),
          router: !hasNews,
          route: 'category',
          params: {
            categoryPath: intl.formatMessage(
              messages['category.press.param'],
            ),
          },
        },
      ],
    },
    ...(isUS ? [
      {
        id: 'education',
        router: true,
        route: 'education',
        items: [],
      },
      {
        id: 'executive',
        router: true,
        route: 'executive',
        items: [],
      },
    ] : []),
    {
      id: 'jobs',
      items: [
        {
          id: 'findAFashionJob',
          href: '/fashionjobs',
          router: jobsLanding,
          route: `jobs-landing.${locale}`,
        },
        {
          id: 'findAnInternship',
          route: `jobs.${locale}`,
          params: {
            categoryParam: intl.formatMessage(
              messages['jobs.category.internships.param'],
            ),
          },
          router: true,
        },
        {
          id: 'postJob',
          href: 'https://fashionunited.com/company/submit-your-job-postings',
          router: isUS,
          route: 'landing',
          params: {
            landingPrefix: 'company',
            path: 'submit-your-job-postings',
          },
        },
        {
          id: 'discoverEmployers',
          href: 'https://fashionunited.com/jobs',
          router: hasJobsInLanding,
          route: 'landing',
          params: {
            landingPrefix: 'jobs',
          },
        },
      ],
    },
    {
      id: 'marketplace',
      href: 'https://fashionunited.com/marketplace',
      router: isUS,
      route: 'landing',
      params: { landingPrefix: 'marketplace' },
      items: [],
    },
    {
      id: 'brandDirectory',
      href: 'https://fashionunited.com/brands',
      router: isUS,
      route: 'brands-directory',
      items: [],
    },
    {
      id: 'events',
      router: true,
      route: 'events',
      items: [],
    },
    {
      id: 'lookbook',
      href: 'https://fashionunited.com/lookbook',
      router: isUS,
      route: 'lookbook',
      items: [],
    },
    {
      id: 'data',
      items: [
        {
          id: 'top100',
          href: 'https://fashionunited.com/i/top100',
          router: isUS,
          route: 'landing',
          params: {
            landingPrefix: 'i',
            path: 'top100',
          },
        },
        {
          id: 'statistics',
          href: 'https://fashionunited.com/global-fashion-industry-statistics',
          router: isUS,
          route: 'landing',
          params: {
            landingPrefix: 'global-fashion-industry-statistics',
          },
        },
        ...(isUS || !hasSpecialPages) ? [{
          id: 'businessData',
          href: 'https://fashionunited.com/i',
          router: isUS,
          route: 'landing',
          params: {
            landingPrefix: 'i',
          },
        }] : [],
        ...hasSpecialPages ? [{
          id: 'businessIntelligence',
          router: hasSpecialPages,
          route: 'specials',
          params: {
            slug: 'data',
          },
          href: '/specials/data',
        }] : [],
      ],
    },
    {
      id: 'trends',
      items: [],
      router: hasNews,
      route: 'trends',
      href: intl.formatMessage(
        messages['trends.href'],
      ),
    },
    ...hasSpecialPages ? [{
      id: 'specials.education',
      items: [],
      router: true,
      route: 'specials',
      params: {
        slug: intl.formatMessage(
          messages['specials.education.slug'],
        ),
      },
      href: intl.formatMessage(
        messages['specials.education.href'],
      ),
    }] : [],
    {
      id: 'newsletter',
      items: [],
      href: intl.formatMessage(
        messages['newsletter.href'],
      ),
    },
    {
      id: 'searchTitle',
      items: [],
      href: intl.formatMessage(
        messages['search.href'],
      ),
      router: !hasNews,
      route: 'search',
    },
  ];
};

export default getDrawer;
